import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAPI } from './adminAPI';

interface AdminAuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if token exists in localStorage on mount
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('adminToken');
      if (savedToken) {
        adminAPI.setToken(savedToken);
        const isValid = await adminAPI.testAuth();
        if (isValid) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('adminToken');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (password: string): Promise<boolean> => {
    try {
      adminAPI.setToken(password);
      const isValid = await adminAPI.testAuth();
      
      if (isValid) {
        localStorage.setItem('adminToken', password);
        setIsAuthenticated(true);
        return true;
      } else {
        adminAPI.clearToken();
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      adminAPI.clearToken();
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    adminAPI.clearToken();
    setIsAuthenticated(false);
  };

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, login, logout, isLoading }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
